export default class PromotionsGrid extends Core.SectionComponent {
  constructor(el, context) {
    super(el, context);

    this.slideContainer = el.querySelector('[data-promo-grid-slider-list]');
    if (!this.slideContainer) return;

    this.slides = this.slideContainer.querySelectorAll('[data-promo-grid-slider-item]');
    this.slideButtonPrevious = el.querySelector('[data-promo-grid-slider-previous]');
    this.slideButtonNext = el.querySelector('[data-promo-grid-slider-next]');
    this.currentSlideIndicator = el.querySelector('[data-promo-grid-slider-navigation-current-slide]');
    this.slideCount = this.data.numberOfSlides;
    this.largestMobileHeightSetting = Number(this.slideContainer.dataset.promoGridLargestSlide);
    this.resizeFunc = this._onResize.bind(this);
    this._observer = new IntersectionObserver(this._handleIntersection.bind(this), {
      root: this.slideContainer,
      rootMargin: '0px 0px 0px 0px',
      threshold: 1.0,
    });

    this.state = {
      selectedSlideIndex: 0,
      numberOfSlidesZeroBased: Number(this.slideCount) - 1,
    };

    this.events.register(this.slideButtonPrevious, 'click', e => {
      e.preventDefault();
      const selectedSlideEl = this.slideContainer.querySelector(`[data-promo-grid-slide-index='${this.state.selectedSlideIndex}']`);
      const currentSlidePosition = Array.prototype.indexOf.call(this.slides, selectedSlideEl);
      const nextSlideIndex = currentSlidePosition - 1;
      const matchingSlide = this.slides[nextSlideIndex];

      if (matchingSlide) {
        this._scrollIntoView(matchingSlide);
      }
    });

    this.events.register(this.slideButtonNext, 'click', e => {
      e.preventDefault();

      const selectedSlideEl = this.slideContainer.querySelector(`[data-promo-grid-slide-index='${this.state.selectedSlideIndex}']`);
      const currentSlidePosition = Array.prototype.indexOf.call(this.slides, selectedSlideEl);
      const nextSlideIndex = currentSlidePosition + 1;
      const matchingSlide = this.slides[nextSlideIndex];

      if (matchingSlide) {
        this._scrollIntoView(matchingSlide);
      }
    });

    this.events.register(this.slideContainer, 'rimg:load', () => this._adjustSectionHeight());

    this.slides.forEach(slide => this._observer.observe(slide));
  }

  _onResize() {
    this._adjustSectionHeight();
    if (this.selectedBlock) {
      this._scrollIntoView(this.selectedBlock);
    }
  }

  onMount() {
    if (!this.slideContainer) return;
    this._adjustSectionHeight();
    Core.Breakpoints.onChange(this.resizeFunc);
  }

  onDismount() {
    super.onDismount();
    Core.Breakpoints.offChange(this.resizeFunc);
  }

  onBlockSelect(e) {
    if (!e) return;
    this.selectedBlock = e.target;
    this._scrollIntoView(e.target);
  }

  /**
   * Adjusts the section height according to the text content within the block.
   * The block with the largest amount of text content sets the height of the slider on mobile
   * On desktop, we calculate the height when the first block in a row is using the 'natural'
   * height setting so that all text content appears.
   */
  _adjustSectionHeight() {
    const editorialBlocks = this.queryAll('block-promo-editorial');
    const textBlocks = this.queryAll('block-promo-text');
    const imageBlocks = this.queryAll('block-promo-image');
    const blockHeights = [...editorialBlocks, ...textBlocks, ...imageBlocks].map(block => Number(block.blockHeight.replace('px', '')));
    const largestBlockHeight = Math.max(...blockHeights);

    if (Core.Breakpoints.max('M')) {
      if (largestBlockHeight > this.largestMobileHeightSetting) {
        this.slideContainer.style.setProperty('--mobile-height', `${largestBlockHeight}px`);
      }
    } else {
      const gridRows = this._calculateGridRowHeights();
      this.slideContainer.style.setProperty('--row-heights', gridRows);
    }
  }

  /**
   * Determine grid row heights based on block setting values.
   * This is specifically for cases where the text content is
   * taller than the grid row
   * @returns {String}
   */
  _calculateGridRowHeights() {
    // Figure out which slides are first in the row by tracking how many columns
    // each block spans. Everytime the column count passes 12 we know that a new row
    // has begun.
    let currentColumnInt = 0;
    let gridRowString = '';
    const { blockHeight } = Core.Components.getComponent(this.slides[0].children[0]);
    const firstBlockSettingHeight = this.slides[0].dataset.promoGridSliderItemHeight;
    gridRowString = firstBlockSettingHeight === 'auto'
      ? blockHeight
      : firstBlockSettingHeight;

    this.slides.forEach(slide => {
      const { promoGridSliderItemWidth, promoGridSliderItemHeight } = slide.dataset;
      const { blockHeight: currentBlockHeight } = Core.Components.getComponent(slide.children[0]);
      const blockColumnInt = Number(promoGridSliderItemWidth);
      let newHeight;
      currentColumnInt += blockColumnInt;

      if (currentColumnInt > 12) {
        newHeight = promoGridSliderItemHeight === 'auto'
          ? currentBlockHeight
          : promoGridSliderItemHeight;

        gridRowString = `${gridRowString} ${newHeight}`;
        currentColumnInt = blockColumnInt;
      }
    });

    return gridRowString;
  }

  /**
   * Callback for IntersectionObserver
   * Handles updating the selected slide state as well as
   * the current slide indicator and button disabled state
   * @param {IntersectionObserverEntry[]} entries -
   */
  _handleIntersection(entries) {
    const intersectingSlideIndex = entries
      .filter(entry => entry.isIntersecting)
      .map(({ target }) => Array.prototype.indexOf.call(this.slides, target));

    if (intersectingSlideIndex.length > 0) {
      this.setState({
        selectedSlideIndex: intersectingSlideIndex[0],
      });
    }
  }

  /**
   * Scrolls the next/previous slide into view
   * @param {HTMLElement} slide
   */
  _scrollIntoView(slide) {
    this.slideContainer.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth',
    });
  }

  onStateChange() {
    this.currentSlideIndicator.innerText = this.state.selectedSlideIndex + 1;
    if (this.state.selectedSlideIndex === 0) {
      this.slideButtonPrevious.setAttribute('disabled', '');
    } else {
      this.slideButtonPrevious.removeAttribute('disabled');
    }

    if (this.state.selectedSlideIndex === this.state.numberOfSlidesZeroBased) {
      this.slideButtonNext.setAttribute('disabled', '');
    } else {
      this.slideButtonNext.removeAttribute('disabled');
    }
  }
}
